import { CircleParking, UserCheck, UserPlus, UsersRound, UserX } from "lucide-react";
import { useEffect, useState } from "react";

const STATS_ICON_SIZE = 26;
const STATS_STROKE = 1.75;

/** Lucide pipeline icons (design guide: outlined UI, stroke 1.75). */
export function RecruiterApplicantIcon({ name, className = "hiring-role-applicant-stats__icon", ...rest }) {
  const p = {
    className,
    size: STATS_ICON_SIZE,
    strokeWidth: STATS_STROKE,
    "aria-hidden": true,
    ...rest,
  };

  switch (name) {
    case "applicants":
      return <UsersRound {...p} />;
    case "rejected-applicants":
      return <UserX {...p} />;
    case "accepted-applicant":
      return <UserCheck {...p} />;
    case "new-user":
      return <UserPlus {...p} />;
    case "park-applicant":
      return <CircleParking {...p} />;
    default:
      return null;
  }
}

export function HiringRoleApplicantStats({
  role,
  defaultSelectedIndex = null,
  flashIndex = null,
  selectable = false,
  onItemClick = null,
  showLabels = true,
}) {
  const items = [
    { icon: "new-user", label: "New applicants", shortLabel: "New", value: role.newApplications ?? 0 },
    {
      icon: "accepted-applicant",
      label: "Shortlisted applicants",
      shortLabel: "Shortlisted",
      value: role.shortlistedApplicants ?? 0,
    },
    { icon: "park-applicant", label: "Parked applicants", shortLabel: "Parked", value: role.parkApplicants ?? 0 },
    {
      icon: "rejected-applicants",
      label: "Rejected applicants",
      shortLabel: "Rejected",
      value: role.rejectedApplications ?? 0,
    },
    { icon: "applicants", label: "All applicants", shortLabel: "All applicants", value: role.applicants ?? 0 },
  ];

  const [selectedIndex, setSelectedIndex] = useState(
    selectable && typeof defaultSelectedIndex === "number" ? defaultSelectedIndex : null,
  );

  useEffect(() => {
    if (!selectable) return;
    setSelectedIndex(typeof defaultSelectedIndex === "number" ? defaultSelectedIndex : null);
  }, [defaultSelectedIndex, selectable]);

  return (
    <div
      className={`hiring-role-applicant-stats${selectable ? " hiring-role-applicant-stats--selectable" : ""}`}
      role="group"
      aria-label="Applicant counts for this role"
    >
      {items.map((item, idx) => {
        const isSelected = selectable && selectedIndex === idx;
        const isFlashing = flashIndex === idx;
        const className = `hiring-role-applicant-stats__cell${
          isSelected ? " hiring-role-applicant-stats__cell--selected" : ""
        }${isFlashing ? " hiring-role-applicant-stats__cell--flash" : ""}`;
        const content = (
          <>
            {showLabels ? (
              <span className="hiring-role-applicant-stats__label">{item.shortLabel.toUpperCase()}</span>
            ) : null}
            <span className="hiring-role-applicant-stats__metric">
              <RecruiterApplicantIcon name={item.icon} className="hiring-role-applicant-stats__icon" />
              <span className="hiring-role-applicant-stats__value" aria-label={`${item.label}: ${item.value}`}>
                {item.value}
              </span>
            </span>
          </>
        );

        if (selectable || typeof onItemClick === "function") {
          return (
            <button
              key={item.label}
              type="button"
              className={className}
              onClick={(event) => {
                if (selectable) setSelectedIndex(idx);
                if (typeof onItemClick === "function") onItemClick(idx, item, event);
              }}
              aria-pressed={selectable ? isSelected : undefined}
              title={item.label}
            >
              {content}
            </button>
          );
        }

        return (
          <div key={item.label} className={className} title={item.label}>
            {content}
          </div>
        );
      })}
    </div>
  );
}
